import React, { useState} from 'react';
import { API } from "aws-amplify";
import Fuse from 'fuse.js';

import { sortArrayOfObjects, calculateConflicts, loadData, getData } from '../js/Helpers';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusSquare, faSortDown, faPeopleArrows, faTrashAlt, faSearch, faClone } from '@fortawesome/free-solid-svg-icons';
import { faSquare as farSquare } from '@fortawesome/free-regular-svg-icons';
import Loader from './hsms/common/Loader';
import { CONFLICT_TYPES } from '../js/Constants';
import { useConflictActions, useConflictData } from './hsms/store/magnetboardStore';
import { filterOutConflicts } from '../js/Conflicts';
import {
	buildRequestSelectionKey,
	duplicateDraftCourseRequests,
	parseRequestSelectionKey,
	removeDraftCourseRequests,
	transferDraftCourseRequests,
} from './hsms/utils/courseRequestDraftHelpers';

export default function BulkEditCourseRequest({courseID, schoolInfo, courses, students, setStudents, sections, setSections, selectedStudents, setSelectedStudents, availableSelectionKeys = null, draftMode = false }) {

	const [confirmBulkRemoveStudentsOpen, setConfirmBulkRemoveStudentsOpen] = useState(false);
	const [otherCoursesToBulkSendRequestsOpen, setOtherCoursesToBulkSendRequestsOpen] = useState(false);
	const [courseReceivingRequests, setCourseReceivingRequests] = useState(null);
	const [requestActionType, setRequestActionType] = useState('transfer');
	const [confirmCourseActionInfo, setConfirmCourseActionInfo] = useState(null);
	const [confirmModalSubmitting, setConfirmModalSubmitting] = useState(null);

	const { setConflicts, setMatchPercent, markConflictDataFresh } = useConflictActions();
	const { conflicts } = useConflictData();


	const toggleSelectAll = () => {
		const any_students_selected = selectedStudents.length > 0;
		
		if(any_students_selected)
		{
			setSelectedStudents([]);
		}
		else
		{
			const students_requesting_course = students.reduce((results, student) =>{
				for (const request_group of student.student_requests) {
					if (!request_group.course_requests || request_group.course_requests.length === 0) continue;
					
					for (const course_request of request_group.course_requests) {
						if (course_request.course_id === courseID && course_request.is_deleted === '0') {
							const composite_key = buildRequestSelectionKey(request_group.group_id, student.student_id);
							results.push(composite_key);
						}
					}
				}
				return results;
			}, []);

			const selection_keys = Array.isArray(availableSelectionKeys)
				? students_requesting_course.filter(composite_key => availableSelectionKeys.includes(composite_key))
				: students_requesting_course;

			setSelectedStudents([...selection_keys]);
		}
	}

	const bulkRemoveStudents = async () => {
		if(draftMode)
		{
			setStudents(current_students => removeDraftCourseRequests(current_students, selectedStudents, courseID));
			setSelectedStudents([]);
			setConfirmBulkRemoveStudentsOpen(false);
			return;
		}

		const group_ids_to_delete = [];
		const student_ids_to_remove_from_sections = new Set();
		const next_students = JSON.parse(JSON.stringify(students));
		const next_sections = sections.map(section => ({
			...section,
			student_list: Array.isArray(section.student_list) ? [...section.student_list] : [],
		}));
		
		selectedStudents.forEach(composite_key => {
			const {group_id, student_id} = parseRequestSelectionKey(composite_key);
			group_ids_to_delete.push(group_id);
			student_ids_to_remove_from_sections.add(student_id);
			
			const student_index = next_students.findIndex(student => student.student_id === student_id);
			
			if(student_index !== -1) {
				for (const request_group of next_students[student_index].student_requests) {
					if (request_group.group_id === group_id && request_group.course_requests && request_group.course_requests.length > 0) {
						const course_request_index = request_group.course_requests.findIndex(
							course_request => course_request.course_id === courseID
						);
						if (course_request_index !== -1) {
							request_group.course_requests[course_request_index].is_deleted = '1';
							break;
						}
					}
				}
			}
		});

		// Remove students from sections if they have no more active requests
		student_ids_to_remove_from_sections.forEach(student_id => {
			const section_index = next_sections.findIndex(section => section.course_id === courseID && section.student_list.includes(student_id));

			if(section_index !== -1)
			{
				const section_info = next_sections[section_index];
				const student_list = section_info.student_list;
				const student_index = student_list.findIndex(student => student === student_id);

				next_sections[section_index].student_list.splice(student_index, 1);
			}
		});

		/// Update BACKEND ///
		const passed_data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'delete', course_id:courseID, group_ids:group_ids_to_delete};
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-requests'
		const myInit = { // OPTIONAL
			response: true,
			body: JSON.stringify(passed_data),
		};
		
		try {
			// Update the requests in the database
			await API.post(apiName, url, myInit);
			setSections(next_sections);
			setStudents(next_students);
			setSelectedStudents([]);

			// Update conflicts
			const conflicts_to_check = [
				CONFLICT_TYPES.STUDENT_DOUBLE_BOOKED,
				CONFLICT_TYPES.STUDENT_CONFLICTING_PERIODS,
				CONFLICT_TYPES.STUDENT_STUDENT_RESTRICTION,
				CONFLICT_TYPES.TEACHER_STUDENT_RESTRICTION,
				CONFLICT_TYPES.COURSE_EXCEEDS_CAPACITY,
				CONFLICT_TYPES.INCLUSION_TOO_MANY_STUDENTS,
				CONFLICT_TYPES.INCLUSION_OVER_HALF
			];

			calculateConflicts({
				school_id: schoolInfo.school_id, 
				schedule_version_id: schoolInfo.current_schedule_version_id,
				conflicts_to_check: conflicts_to_check,
				match_percent: true
			}).then(data => {
				const new_conflicts = data.conflicts;
				const match_percent = data.match_percent;
				const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
				setConflicts([...filtered_out_conflicts, ...new_conflicts]);
				setMatchPercent(match_percent);
				markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
			});
		} catch(e)
		{
			console.log(e);
		}
	}

	// Component for bulk sending students to other course request
	// Need to separate this out so it updates on state change in the modal (needed for search)
	const OtherCourseList = () => {
		const [otherCoursesSearchResults, setOtherCoursesSearchResults] = useState([...courses]);
		const isDuplicateAction = requestActionType === 'duplicate';
		const actionLabel = isDuplicateAction ? 'Duplicate Requests' : 'Transfer Requests';
		const actionDescription = isDuplicateAction
			? 'What course should these course requests also be added to?'
			: 'What course should these course requests be transferred to?';

		const otherCoursesSearch = (e) => {
			const search_value = e.target.value;
			
			let course_results = [];

			if(search_value.length === 0)
			{
				course_results = courses;
			}
			else
			{
				const fuse = new Fuse(courses, {
					keys: ['name', 'course_code'],
					threshold: .1
				});
				
				const results = fuse.search(search_value);
				course_results = results.map(result => result.item).slice(0, 6);
			}

			setOtherCoursesSearchResults([...course_results]);
		}

		return (
			<div className='text-align-left'>
				<h1 style={{margin:'0px'}}>Choose a Course</h1>
				<p style={{margin:'10px 0px 20px 0px'}}>{actionDescription}</p>
				<div className='search-add-adding-input-container' style={{marginBottom:'10px'}}>
					<input className='search-add-adding-input' onChange={(e) => otherCoursesSearch(e)} placeholder='Search for a course'/>
					<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
				</div>
				<div className='edit-course-move-student-request-row' style={{backgroundColor:'#fff'}}>
					<div><strong><u>Course Code</u></strong></div>
					<div><strong><u>Course</u></strong></div>
					<div className='white-text'>.</div>
				</div>
				{courses.filter(course => otherCoursesSearchResults.findIndex(other_course => other_course.course_id === course.course_id) !== -1).map((course, i) => {
					const course_id = course.course_id;
					const course_info = (course_id) ? courses.find(course => course.course_id === course_id) : null;
					const course_code = (course_info) ? course_info.course_code : null;
					const course_name = (course_info) ? course_info.name : null;

					const course_receiving_requests = (courseReceivingRequests === course_id);

					return (
						<div key={i} className='edit-course-move-student-request-row'>
							<div>{course_code}</div>
							<div>{course_name}</div>
							{(course_id === courseID) ?
								(
									<div className='small-text align-right'>Current Course</div>
								):
								(
									course_receiving_requests ? <Loader size={'32px'} style={{ marginRight: '15px', float: 'right' }} /> : <div className={`btn-small btn-round blue-btn align-right ${courseReceivingRequests && 'opacity-75'}`} onClick={courseReceivingRequests ? ()=>{} : () => confirmCourseAction(course_id, course_name, course_code)}>{actionLabel}</div>
								)
							}
						</div>
					)
				})}
			</div>
		)
	}

	const openTransferConfirmation = (target_course_id, target_course_name, target_course_code) => {
		setOtherCoursesToBulkSendRequestsOpen(false);
		setConfirmCourseActionInfo({
			type: 'transfer',
			target_course_id,
			target_course_name,
			target_course_code,
		});
	}

	const openDuplicateConfirmation = (target_course_id, target_course_name, target_course_code) => {
		setOtherCoursesToBulkSendRequestsOpen(false);
		setConfirmCourseActionInfo({
			type: 'duplicate',
			target_course_id,
			target_course_name,
			target_course_code,
		});
	}

	const confirmCourseAction = (target_course_id, target_course_name, target_course_code) => {
		if(requestActionType === 'duplicate') {
			openDuplicateConfirmation(target_course_id, target_course_name, target_course_code);
			return;
		}

		openTransferConfirmation(target_course_id, target_course_name, target_course_code);
	}

	const closeConfirmCourseActionModal = () => {
		if(confirmModalSubmitting) return;
		setConfirmCourseActionInfo(null);
	}

	const runConfirmedCourseAction = async () => {
		if(!confirmCourseActionInfo) return;

		const { type, target_course_id } = confirmCourseActionInfo;
		setConfirmModalSubmitting(type);

		try {
			if(type === 'duplicate') {
				await bulkDuplicateStudentsToNewCourseRequest(target_course_id);
			}
			else {
				await bulkSendStudentsToNewCourseRequest(target_course_id);
			}
		} finally {
			setConfirmModalSubmitting(null);
			setConfirmCourseActionInfo(null);
		}
	}

	const confirmDeleteRequests = async () => {
		setConfirmModalSubmitting('delete');

		try {
			await bulkRemoveStudents();
		} finally {
			setConfirmModalSubmitting(null);
			setConfirmBulkRemoveStudentsOpen(false);
		}
	}

	const bulkSendStudentsToNewCourseRequest = async (new_course_id) => {
		if(draftMode)
		{
			setStudents(current_students => transferDraftCourseRequests(current_students, selectedStudents, courseID, new_course_id));
			setSelectedStudents([]);
			setCourseReceivingRequests(null);
			setOtherCoursesToBulkSendRequestsOpen(false);
			return;
		}

		setCourseReceivingRequests(new_course_id);

		const student_ids_set = new Set();
		const group_ids_set = new Set();
		selectedStudents.forEach(composite_key => {
			const {student_id, group_id} = parseRequestSelectionKey(composite_key);
			student_ids_set.add(student_id);
			group_ids_set.add(group_id);
		});
		const student_ids_array = Array.from(student_ids_set);
		const group_ids_array = Array.from(group_ids_set);

		///////////////////////////
		///// SEND TO BACKEND /////
		///////////////////////////
		const data = {
			school_id: schoolInfo.school_id, 
			schedule_version_id: schoolInfo.current_schedule_version_id, 
			student_list: student_ids_array, 
			group_ids: group_ids_array,
			old_course_id: courseID, 
			new_course_id: new_course_id
		};
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/move-students-to-new-course-request';
		const myInit = { // OPTIONAL
			response: true,
			body: JSON.stringify(data),
		};
		
		try {
			// MOVE STUDENTS TO NEW COURSE REQUEST
			await API.post(apiName, url, myInit);

			const [refreshed_students, refreshed_sections_bundle] = await Promise.all([
				loadData({
					school_id: schoolInfo.school_id,
					schedule_version_id: schoolInfo.current_schedule_version_id,
					data_version_id: schoolInfo.current_data_version_id,
					data_types: ['students'],
				}),
				getData('sections', '/get-sections', {
					school_id: schoolInfo.school_id,
					schedule_version_id: schoolInfo.current_schedule_version_id,
					data_version_id: schoolInfo.current_data_version_id,
					database: 'edario',
				}, true),
			]);

			if (refreshed_students && Array.isArray(refreshed_students.students)) {
				setStudents([...refreshed_students.students]);
			}

			if (refreshed_sections_bundle && Array.isArray(refreshed_sections_bundle.sections)) {
				setSections([...refreshed_sections_bundle.sections]);
			}

			// Update conflicts
			const conflicts_to_check = [
				CONFLICT_TYPES.STUDENT_DOUBLE_BOOKED,
				CONFLICT_TYPES.STUDENT_CONFLICTING_PERIODS,
				CONFLICT_TYPES.STUDENT_STUDENT_RESTRICTION,
				CONFLICT_TYPES.TEACHER_STUDENT_RESTRICTION,
				CONFLICT_TYPES.COURSE_EXCEEDS_CAPACITY,
				CONFLICT_TYPES.INCLUSION_TOO_MANY_STUDENTS,
				CONFLICT_TYPES.INCLUSION_OVER_HALF
			]
			calculateConflicts({
				school_id: schoolInfo.school_id, 
				schedule_version_id: schoolInfo.current_schedule_version_id, 
				conflicts_to_check: conflicts_to_check, 
				match_percent: true
			}).then(data => {
				const new_conflicts = data.conflicts;
				const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
				setConflicts([...filtered_out_conflicts, ...new_conflicts]);
				setMatchPercent(data.match_percent);
				markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
			});

			setSelectedStudents([]);
			setCourseReceivingRequests(null);
			setOtherCoursesToBulkSendRequestsOpen(false);
		} catch(error)
		{
			console.log(error.response);
			setCourseReceivingRequests(null);
		}
	}

	const bulkDuplicateStudentsToNewCourseRequest = async (new_course_id) => {
		if(draftMode)
		{
			setStudents(current_students => duplicateDraftCourseRequests(current_students, selectedStudents, new_course_id));
			setSelectedStudents([]);
			setCourseReceivingRequests(null);
			setOtherCoursesToBulkSendRequestsOpen(false);
			return;
		}

		setCourseReceivingRequests(new_course_id);

		const student_ids_set = new Set();
		const group_ids_set = new Set();
		selectedStudents.forEach(composite_key => {
			const {student_id, group_id} = parseRequestSelectionKey(composite_key);
			student_ids_set.add(student_id);
			group_ids_set.add(group_id);
		});

		const data = {
			school_id: schoolInfo.school_id,
			schedule_version_id: schoolInfo.current_schedule_version_id,
			student_list: Array.from(student_ids_set),
			group_ids: Array.from(group_ids_set),
			old_course_id: courseID,
			new_course_id: new_course_id,
		};
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/duplicate-students-to-new-course-request';
		const myInit = {
			response: true,
			body: JSON.stringify(data),
		};

		try {
			await API.post(apiName, url, myInit);

			const refreshed_students = await loadData({
				school_id: schoolInfo.school_id,
				schedule_version_id: schoolInfo.current_schedule_version_id,
				data_version_id: schoolInfo.current_data_version_id,
				data_types: ['students'],
			});

			if (refreshed_students && Array.isArray(refreshed_students.students)) {
				setStudents([...refreshed_students.students]);
			}

			const conflicts_to_check = [
				CONFLICT_TYPES.STUDENT_DOUBLE_BOOKED,
				CONFLICT_TYPES.STUDENT_CONFLICTING_PERIODS,
				CONFLICT_TYPES.STUDENT_STUDENT_RESTRICTION,
				CONFLICT_TYPES.TEACHER_STUDENT_RESTRICTION,
				CONFLICT_TYPES.COURSE_EXCEEDS_CAPACITY,
				CONFLICT_TYPES.INCLUSION_TOO_MANY_STUDENTS,
				CONFLICT_TYPES.INCLUSION_OVER_HALF
			];
			calculateConflicts({
				school_id: schoolInfo.school_id,
				schedule_version_id: schoolInfo.current_schedule_version_id,
				conflicts_to_check: conflicts_to_check,
				match_percent: true
			}).then(data => {
				const new_conflicts = data.conflicts;
				const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
				setConflicts([...filtered_out_conflicts, ...new_conflicts]);
				setMatchPercent(data.match_percent);
				markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
			});

			setSelectedStudents([]);
			setCourseReceivingRequests(null);
			setOtherCoursesToBulkSendRequestsOpen(false);
		} catch(error) {
			console.log(error.response);
			setCourseReceivingRequests(null);
		}
	}

	const course_info = courses.find(course => course.course_id === courseID);
	const course_name = (course_info) ? course_info.name : null;
	const course_code = (course_info) ? course_info.course_code : null;

	// Extract unique student_ids from composite keys
	const selected_student_ids = new Set();
	selectedStudents.forEach(composite_key => {
		const {student_id} = parseRequestSelectionKey(composite_key);
		selected_student_ids.add(student_id);
	});

	const students_being_removed = students.filter(student => {
		if (!selected_student_ids.has(`${student.student_id}`)) return false;

		return student.student_requests.some(request_group => {
			if (!request_group.course_requests || request_group.course_requests.length === 0) return false;
			return request_group.course_requests.some(course_request => 
				course_request.course_id === courseID && course_request.is_deleted === '0'
			);
		});
	});
	const sorted_students_being_removed = (students_being_removed.length > 0) ? sortArrayOfObjects(students_being_removed, 'last_name', 'text', 'asc') : [];
	const confirmActionStudentNames = sorted_students_being_removed.map((student_info, index) => {
		const student_id = (student_info) ? student_info.student_id : index;
		const student_first_name = (student_info) ? student_info.first_name : null;
		const student_last_name = (student_info) ? student_info.last_name : null;
		const student_full_name = (student_first_name && student_last_name) ? `${student_last_name}, ${student_first_name}` : null;

		return {
			student_id,
			student_full_name,
		};
	});

	return (
		<>
			<div className='select-all-row' style={{display:'flex', alignItems:'center', columnGap:'10px', flexWrap:'nowrap'}}>
				<div className={`select-all-container ${selectedStudents.length > 0 && 'blue-text'}`} onClick={toggleSelectAll}>
					<FontAwesomeIcon icon={selectedStudents.length > 0 ? faMinusSquare : farSquare}/>
					<FontAwesomeIcon className='select-all-arrow' icon={faSortDown}/>
				</div>
				{selectedStudents.length > 0 &&
					<div className='small-text dark-gray-text' style={{alignSelf:'center', whiteSpace:'nowrap'}}>
						{selectedStudents.length} selected
					</div>
				}
						{selectedStudents.length > 0 &&
					<div className='select-all-actions-container' style={{display:'flex', alignItems:'center', columnGap:'12px'}}>
						<div className='tooltip' data-tooltip-text='Transfer requests to another course' onClick={() => { setRequestActionType('transfer'); setOtherCoursesToBulkSendRequestsOpen(true); }}>
							<FontAwesomeIcon className='select-all-action-btn tooltip' icon={faPeopleArrows}/>
						</div>
						<div className='tooltip' data-tooltip-text='Duplicate requests to another course' onClick={() => { setRequestActionType('duplicate'); setOtherCoursesToBulkSendRequestsOpen(true); }}>
							<FontAwesomeIcon className='select-all-action-btn tooltip' icon={faClone}/>
						</div>
						<div className='tooltip' data-tooltip-text='Delete selected requests' onClick={() => setConfirmBulkRemoveStudentsOpen(true)}>
							<FontAwesomeIcon className='select-all-action-btn tooltip' icon={faTrashAlt}/>
						</div>
					</div>
				}
			</div>
			{otherCoursesToBulkSendRequestsOpen &&
				<div className='modal'>
					<div className='modal-content' style={{width:'700px'}}>
						<span className="modal-close-right" onClick={() => setOtherCoursesToBulkSendRequestsOpen(false)}>&times;</span>
						<OtherCourseList />
					</div>
				</div>
			}
			{confirmCourseActionInfo &&
				<div className='modal'>
					<div className='modal-content'>
						<span className="modal-close-right" onClick={closeConfirmCourseActionModal}>{confirmModalSubmitting ? '' : '\u00d7'}</span>
						<h1>Are you sure?</h1>
						{confirmCourseActionInfo.type === 'duplicate' ? (
							<>
								<p>Do you really want to duplicate the following {selectedStudents.length} request{selectedStudents.length === 1 ? '' : 's'} from {course_name} ({course_code}) to {confirmCourseActionInfo.target_course_name} ({confirmCourseActionInfo.target_course_code})?</p>
								<p className='dark-gray-text'>This will keep the current requests and add the same students to the new course.</p>
							</>
						) : (
							<>
								<p>Do you really want to transfer the following {selectedStudents.length} request{selectedStudents.length === 1 ? '' : 's'} from {course_name} ({course_code}) to {confirmCourseActionInfo.target_course_name} ({confirmCourseActionInfo.target_course_code})?</p>
								<p className='red-text'>This will remove those requests from {course_name} and move them to the new course.</p>
							</>
						)}
						<div className='text-align-left'>
							<div className='modal-example-list'>
								{confirmActionStudentNames.map(({student_id, student_full_name}) => (
									<div key={student_id} className='modal-example-row'>{student_full_name}</div>
								))}
							</div>
						</div>
						<div className='text-align-right'>
							<h3>Total: {selectedStudents.length} request{selectedStudents.length === 1 ? '' : 's'}</h3>
							<div className='small-text dark-gray-text'>{selected_student_ids.size} student{selected_student_ids.size === 1 ? '' : 's'} affected</div>
						</div>
						<div className='modal-btn-container'>
							<div className={`btn gray-btn ${confirmModalSubmitting ? 'opacity-75' : ''}`} onClick={closeConfirmCourseActionModal}>Cancel</div>
							<div className={`btn blue-btn ${confirmModalSubmitting ? 'opacity-75' : ''}`} onClick={confirmModalSubmitting ? ()=>{} : runConfirmedCourseAction} style={{display:'flex', alignItems:'center', justifyContent:'center', columnGap:'10px', minWidth:'190px'}}>
								{confirmModalSubmitting === confirmCourseActionInfo.type && <Loader size={'18px'} />}
								<div>
									{confirmModalSubmitting === confirmCourseActionInfo.type
										? (confirmCourseActionInfo.type === 'duplicate' ? 'Duplicating...' : 'Transferring...')
										: (confirmCourseActionInfo.type === 'duplicate' ? 'Duplicate Requests' : 'Transfer Requests')}
								</div>
							</div>
						</div>
					</div>
				</div>
			}
			{confirmBulkRemoveStudentsOpen &&
				<div className='modal'>
					<div className='modal-content'>
						<span className="modal-close-right" onClick={confirmModalSubmitting ? ()=>{} : () => setConfirmBulkRemoveStudentsOpen(false)}>{confirmModalSubmitting ? '' : '\u00d7'}</span>
						<h1>Are you sure?</h1>
						<p>Do you really want to delete the following {sorted_students_being_removed.length} request{sorted_students_being_removed.length === 1 ? '' : 's'} from {course_name} ({course_code})?</p>
						<p className='red-text'>This will also remove those students from any current section of {course_name}.</p>
						<div className='text-align-left'>
							<div className='modal-example-list'>
								{sorted_students_being_removed.map((student_info, index) => {
									const student_id = (student_info) ? student_info.student_id : index;
									const student_first_name = (student_info) ? student_info.first_name : null;
									const student_last_name = (student_info) ? student_info.last_name : null;
									const student_full_name = (student_first_name && student_last_name) ? `${student_last_name}, ${student_first_name}` : null;

									return (
										<div key={student_id} className='modal-example-row'>{student_full_name}</div>
									)
								})}
							</div>
						</div>
						<div className='text-align-right'>
							<h3>Total: {sorted_students_being_removed.length} students</h3>
						</div>
						<div className='modal-btn-container'>
							<div className={`btn gray-btn ${confirmModalSubmitting ? 'opacity-75' : ''}`} onClick={confirmModalSubmitting ? ()=>{} : () => setConfirmBulkRemoveStudentsOpen(false)}>Cancel</div>
							<div className={`btn red-btn ${confirmModalSubmitting ? 'opacity-75' : ''}`} onClick={confirmModalSubmitting ? ()=>{} : confirmDeleteRequests} style={{display:'flex', alignItems:'center', justifyContent:'center', columnGap:'10px', minWidth:'170px'}}>
								{confirmModalSubmitting === 'delete' && <Loader size={'18px'} />}
								<div>{confirmModalSubmitting === 'delete' ? 'Deleting...' : 'Delete Requests'}</div>
							</div>
						</div>
					</div>
				</div>
			}
		</>
		
	);
}
